import axios from 'axios';
import { feathers } from '@/feathers-client';
import { convert12To24 } from '@/Core/filters/dates';
import actionTableHeaders from './../config/table-headers/actions';
import commonActions, { formatActionSchedule } from './common-actions';
import { AGENT_ACTION_TYPES, TIMING_TYPES, getActionType } from './../config/actions';


const FILE_ID_DELIMITER = ',';
const TYPE_TASK = getActionType('task');
const TYPE_TEXT = getActionType('text');
const RECURRENCE_PLACEHOLDER = 'Legacy_Recurrence_Placeholder';

/**
 * @param {Array} requiredParams - an array of the names of the required fields
 * @param {Object} payload - the params that were submitted from the frontend
 * @param {Function} commit - vuex function to call a mutation
 */
const checkRequiredFields = (requiredParams, payload, commit) => {
  if (payload.template_type === 'Email') requiredParams.push('subject');

  const missingFields = requiredParams.filter(param => {
    return !payload[param];
  });
  if (missingFields.length) {
    const error = new Error(`Missing ${missingFields.join(', ')}`);

    commit('setActionError', error);
    throw error;
  }
};
/*
 * The campaign actions module
 */
export default {
  modules: {
    getAction: {
      namespaced: true,
      state: {
        actions: [],
        error: null,
        totals: 0,
        isLoading: false,
        headers: actionTableHeaders,
        activeContacts: {
          data: null,
          total: 0,
          limit: 15,
          skip: 0,
        },
      },

      getters: {
        ...commonActions.getters,
        actions(state, getters) {
          if (state.actions && getters.visibleFields) {
            const newActionsList = state.actions.map(action => {
              return Object.assign({}, ...getters.visibleFields.map(key => ({
                [key]: action[key],
              })));
            });
            return newActionsList;
          }
        },
      },

      mutations: {
        setActions(state, payload) {
          state.actions = payload;
        },
        setActionsError(state, payload) {
          state.error = payload;
        },
        setIsLoading(state, payload) {
          state.isLoading = payload;
        },
        setTotals(state, payload) {
          state.totals = payload;
        },
        setActiveContacts(state, payload) {
          const { data, total } = payload;
          state.activeContacts.total = total;
          state.activeContacts.data = data;
        },
        setActiveContactsSkip(state, payload) {
          state.activeContacts.skip = payload;
        },
        setActiveContactsLimit(state, payload) {
          state.activeContacts.limit = payload;
        },
      },

      actions: {
        formatActionData({ commit, getters }, payload) {
          const actions = payload.map(item => {
            switch (item.assigned_user_id) {
              case null:
                item.assigned_user = 'Me';
                break;
              case 0:
                item.assigned_user = 'Campaign initiator';
                break;
              case -1:
                item.assigned_user = 'Contact\'s assigned user';
                break;
              default: {
                const { first_name: firstName, last_name: lastName } = item.assigned_user;
                const newName = `${firstName} ${lastName}`;
                item.assigned_user = newName;
              }
            }

            if (item.template) {
              const { description: templateName, subject } = item.template;
              item.template_name = templateName;
              item.template_subject = subject;
            }
            formatActionSchedule(item);
            return item;
          });
          commit('setActions', actions);
          commit('setIsLoading', false);
        },
        async getAction({ dispatch, commit, getters, state }, payload) {
          let response;
          try {
            response = await feathers.service(`campaigns/${payload}/actions?$load=template`).get();
            commit('setAction', response.data);
          } catch (error) {
            commit('setActionError', error);
          }
        },
        async deleteAction({ dispatch, commit, getters, state }, payload) {
          const { actionId, campaignId, query } = payload;
          try {
            await feathers.service(`campaigns/${campaignId}/actions`).remove(actionId);
          } catch (error) {
            commit('setActionError', error);
          }
          dispatch('findActions', { id: campaignId, query });
        },
        async bulkDeleteAction({ commit }, payload) {
          try {
            commit('setIsLoading', true);
            await axios.put(`${process.env.VUE_APP_ACCOUNTS_API}/users/campaigns/delete-actions`, payload);
          } catch (error) {
            commit('setActionError', error);
          } finally {
            commit('setIsLoading', false);
          }
        },
        async findActions({ commit, dispatch, getters, state }, payload) {
          const { id, query } = payload;
          let response;
          try {
            commit('setIsLoading', true);
            response = await feathers.service(`campaigns/${id}/actions`).find({
              query: { ...query, $load: 'template,assigned_user' },
            });
            dispatch('formatActionData', response.data);
            commit('setTotals', response.total);
          } catch (error) {
            commit('setActionsError', error);
          }
        },
        async getActiveContacts({ commit, state }, payload) {
          const { campaignID, actionID } = payload;
          const { skip, limit } = state.activeContacts;
          const paginationQuery = `?$skip=${skip}&$limit=${limit}`;
          let response;
          try {
            commit('setIsLoading', true);
            response = await axios.get(
              `${process.env.VUE_APP_CONTACTS_MS_BASE_URL}/campaign/${campaignID}/action/${actionID}/contacts${paginationQuery}`,
            );
            commit('setActiveContacts', response.data);
            commit('setIsLoading', false);
          } catch (error) {
            commit('setActionsError', error);
          }
        },
      },
    },
    campaignAction: {
      namespaced: true,
      state: {
        actionError: false,
        actionErrorMessage: '',
        actionSuccess: false,
        actionSuccessMessage: '',
        /**
         * Expects and object like follows:
         * {
         *  key: 0, // number
         *  text: 'email', // string
         *  icon: 'emial', // optional icon
         * }
         */
        displayTemplates: [],
        isLoading: false,
        campaignId: null,
        teammates: [],
      },
      getters: {
        templateFolders(state) {
          return state.displayTemplates.map(folder => {
            return {
              text: folder.name,
              value: folder.id,
            };
          });
        },
        hasTeammate(state) {
          return !!state.teammates.length;
        },
      },

      mutations: {
        setActionType(state, payload) {
          state.actionType = payload;
        },
        clearErrors(state) {
          state.actionError = false;
          state.actionErrorMessage = '';
        },
        setIsLoading(state, payload) {
          state.isLoading = payload;
        },
        setActionError(state, payload) {
          state.actionError = false;
          state.actionErrorMessage = '';
          state.actionError = true;
          state.actionErrorMessage = payload.message;
        },
        clearSuccess(state) {
          state.actionSuccess = false;
          state.actionSuccessMessage = '';
        },
        setActionSuccess(state, payload) {
          state.actionSuccess = true;
          state.actionSuccessMessage = payload;
        },
        assignTemplatesToFolder(state, payload) {
          const {
            desiredFolder,
            templates,
          } = payload;
          state.displayTemplates.forEach(folder => {
            if (folder.id === desiredFolder.id) {
              let processedTemplates = [];
              if (templates && Array.isArray(templates)) {
                // Sets the display name to be the description of each template
                processedTemplates = templates.map(template => {
                  template.name = template.description;
                  return template;
                });
              }
              folder.children = processedTemplates;
            }
          });
        },
        setCampaignId(state, payload) {
          state.campaignId = payload;
        },
        setTeammates(state, teammates) {
          state.teammates = teammates;
        },
      },

      actions: {
        clearErrors({ commit }) {
          commit('clearErrors');
        },
        clearSuccess({ commit }) {
          commit('clearSuccess');
        },
        /**
         * @returns {Array} The list of template folders
         * Obtains a list of all template folders.
         */
        async setTemplateFolders({ rootState, commit }) {
          try {
            const response = await feathers.service('template-folders').find({
              query: { $paginate: 'false' },
            });
            commit('setActionFolders', response);
          } catch (error) {
            return commit('setActionError', error);
          }
        },
        /**
         * @param {Object} templateFolder, The desired template folder to search on
         * @returns {Array} the templates in that folder
         * Calls the feathers backend to obtain the templates based on the current desiredFolder
         */
        async getTemplatesInFolder({ rootState, commit, state }, templateFolder) {
          const query = {
            $paginate: 'false',
            template_folder_id: { $in: templateFolder.id },
          };
            // Add filter on action type if present
          if (state.actionType && state.actionType.db) {
            query.template_type = { $in: state.actionType.db };
          }
          try {
            const response = await feathers.service('templates').find({
              query,
            });
            return response;
          } catch (error) {
            return commit('setActionError', error);
          }
        },

        /**
         * @param {number} The ID of the current user - omit from the list of returned users (?)
         * @returns {Array} The list of team members available
         */
        async getTeammates({ commit }, currentUserId) {
          try {
            const response = await feathers.service('team-members').find({
              query: {
                $limit: 50,
                request_status: 'Accepted',
                'user.id': { $ne: currentUserId },
              },
            });
            if (response && response.data && response.data.length) {
              // The user info is in the User field of the teammate object
              const teammates = response.data.map(teammate => {
                return teammate.user;
              });
              commit('setTeammates', teammates);
            }
          } catch (error) {
            commit('setTeammates', []);
            return commit('setActionError', error);
          }
        },
        /**
         * Formats the action body to be the desired
         */
        formatActionBody({ commit }, {
          actionType,
          actionInstructions,
          actionReminder,
          actionTiming,
          actionAssignee,
          actionTemplate,
        }) {
          commit('clearErrors');
          commit('clearSuccess');
          let type = actionType.createAction;
          let instructions = '';
          let stopOnTextReply = false;

          // This needs to be converted because the BE doesn't have an 'immediately' timing type.
          // So instead, we pass in as DELAY_BY_MINUTES and set the delay_by_seconds = 0
          if (actionTiming.timing_type.name === TIMING_TYPES.IMMEDIATELY.name) {
            actionTiming.timing_type = TIMING_TYPES.DELAY_BY_MINUTES;
            actionTiming.delay_by_seconds = 0;
          }

          // If the desired action type is Task, set the instruction and type accordingly
          if (actionType.text === TYPE_TASK) {
            instructions = actionInstructions;
            type = AGENT_ACTION_TYPES[actionReminder] || AGENT_ACTION_TYPES.none;
            // If the desired action type is Text, set the stopOnTextReply boolean accordingly
          } else if (actionType.text === TYPE_TEXT) {
            stopOnTextReply = actionTiming.stop_on_text_reply;
          }

          // Structure the payload body
          return {
            type,
            assigned_user_id: actionAssignee.id,
            template_id: actionTemplate.id || null,
            timing_type: actionTiming.timing_type.name,
            is_stopped_on_text_reply: stopOnTextReply,
            dispatch_time: convert12To24(actionTiming.dispatch_time),
            delay_by_days: actionTiming.delay_by_days,
            delay_by_seconds: actionTiming.delay_by_seconds,
            dispatch_datetime: actionTiming.dispatch_datetime,
            recurrence: actionTiming.recurrence_rule,
            notes: instructions,
            event_trigger_type: actionTiming.event_trigger_type,
          };
        },
        /**
         * Submits the new action to the backend
         */
        async submitAction({ state, commit, dispatch }, {
          actionType,
          actionInstructions,
          actionReminder,
          actionTiming,
          actionAssignee,
          actionTemplate,
        }) {

          const request = await dispatch('formatActionBody', {
            actionType,
            actionInstructions,
            actionReminder,
            actionTiming,
            actionAssignee,
            actionTemplate,
          });

          try {
            const response = await feathers.service(`/campaigns/${state.campaignId}/actions`).create(request);
            if (response && response.id) {
              commit('setActionSuccess', `Successfully created a ${response.type} action`);
            }
            return response;
          } catch (error) {
            commit('setActionError', error);
          }
        },
        /**
         * Updates the new action to the backend
         */
        async updateAction({ state, commit, dispatch }, {
          actionId,
          actionType,
          actionInstructions,
          actionReminder,
          actionTiming,
          actionAssignee,
          actionTemplate,
        }) {

          const request = await dispatch('formatActionBody', {
            actionType,
            actionInstructions,
            actionReminder,
            actionTiming,
            actionAssignee,
            actionTemplate,
          });

          try {
            const response = await feathers.service(`/campaigns/${state.campaignId}/actions`).patch(
              actionId,
              request,
            );
            if (response && response.id) {
              commit('setActionSuccess', `Successfully updated a ${response.type} action`);
            }
            return response;
          } catch (error) {
            commit('setActionError', error);
          }
        },
        /**
         * @returns {String} The campaign Name
         *
         * Returns the name of the desired campaign ID
         */
        async getCampaignName({ state, commit }) {
          if (state.campaignId) {
            try {
              const response = await feathers.service('campaigns').get(state.campaignId);
              if (response) return response.name;
            } catch (error) {
              return commit('setActionError', error);
            }
          }
        },
        /**
         * Gets the details of an action within a campaign
         *
         * @param {Number} payload.campaignId Where that action is located in a campaign
         * @param {Number} payload.actionId is the id of the desired action
         */
        async getAction({ commit }, { campaignId, actionId }) {
          commit('setIsLoading', true);
          return feathers.service(`campaigns/${campaignId}/actions`)
            .find({
              query: { id: actionId, $load: 'template' },
            })
            .then(res => {
              if (res.data && res.data.length) {
                return res.data[0];
              }
              return null;
            })
            .catch(err => {
              commit('setActionError', err);
              return null;
            });
        },
        /*
        * Updates a template with the current data the user inputs
        */
        async updateTemplate({ commit, dispatch }, payload) {
          commit('clearErrors');
          commit('clearSuccess');

          // Destructure the payload and assign necessary variables
          const { id, template_folder_id: templateFolderId, body, subject } = payload;
          let { files } = payload;
          if (!files) files = [];

          const requiredParams = ['id', 'template_folder_id', 'body', 'description'];

          checkRequiredFields(requiredParams, payload, commit);

          const description = payload.description || subject;

          // Call the /templates endpoint to PATCH a template.
          let response;
          try {
            response = await feathers.service('templates').patch(id, {
              template_folder_id: templateFolderId,
              subject,
              description,
              body,
              file_id: files.map(file => file.id).join(FILE_ID_DELIMITER),
            });
            // Updates necessary variables
            commit('setActionSuccess', `Successfully updated ${response.description}`);
            return response;
          } catch (error) {
            commit('setActionError', 'Failed to update template');
          }
        },

        /**
         * Creates the template
         *
         * @param {Number} payload Contains template_folder_id, body, and description
         */
        async createTemplate({ commit, dispatch }, payload) {
          commit('clearErrors');
          commit('clearSuccess');

          const requiredParams = ['template_folder_id', 'body', 'description'];
          checkRequiredFields(requiredParams, payload, commit);

          if (payload.files) {
            payload.file_id = payload.files.map(file => file.id).join(FILE_ID_DELIMITER);
          }
          delete payload.files;

          let response;
          try {
            response = await feathers.service('templates').create(payload);
            // Updates necessary variables
            commit('setActionSuccess', `Successfully created ${response.description}`);
            // return response for the front end
            return response;
          } catch (error) {
            commit('setActionError', error);
          }
        },

        /**
         * Gets the teammate information from the backend
         *
         * @param {Number} assignedUserId the user ID we want to look up
         */
        async getTeammatesInfo({ commit }, assignedUserId) {
          try {
            const response = await feathers.service('team-members').find({
              query: {
                request_status: 'Accepted',
                'user.id': assignedUserId,
              },
            });
            if (response.data && response.data.length) {
              const firstTeamMember = response.data[0];
              return firstTeamMember.user;
            }
          } catch (error) {
            commit('setActionError', error);
          }
        },

        /**
         * Gets the template information from the backend
         *
         * @param {Number} templateId the template ID we want to look up
         */
        async getTemplateInfo({ commit }, templateId) {
          try {
            const response = await feathers.service('templates').get(templateId);
            return response;
          } catch (error) {
            const message = 'Current template cannot be loaded. Please select a new template';
            commit('setActionError', { message });
          }
        },
      },
    },
  },
};
